import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../api/client';

export const STEP_ORDER = ['LOCATION', 'INITIAL_NOTICE', 'APPLICATION_QUESTIONS', 'APPLICATION_REVIEW', 'SALES_RESOURCES', 'AGREEMENT', 'TAX_FORM', 'DOCUMENT_REVIEW', 'ONBOARDING_WELCOME', 'GET_LEADS', 'PREPARE', 'MAKE_THE_CALL', 'FOLLOW_UP', 'COMPLETE'] as const;
export type StepKey = (typeof STEP_ORDER)[number];

export interface ProgressData {
  roleId: string;
  // This application's position is no longer offered and it cannot be
  // continued. False once onboarding was completed under it - there is nothing
  // left to continue, so that access is not taken away.
  positionDiscontinued: boolean;
  assignedServiceName: string | null;
  /** The portal's outreach method (e.g. COLD_CALL): the onboarding steps shown are its. */
  outreachMethod?: string;
  outreachMethodLabel?: string;
  currentStep: StepKey;
  highestUnlockedStepIndex: number;
  flags: {
    locationDone: boolean;
    initialNoticeDone: boolean;
    applicationQuestionsDone: boolean;
    applicationApproved: boolean;
    salesResourcesDone: boolean;
    agreementDone: boolean;
    taxFormDone: boolean;
    documentsApproved: boolean;
    onboardingWelcomeDone: boolean;
    leadsDone: boolean;
    prepareDone: boolean;
    callDone: boolean;
    followUpDone: boolean;
  };
  completedAt: string | null;
  documents: {
    agreement: { status: 'PENDING' | 'APPROVED' | 'DENIED' | null; reasons: string[] };
    w8ben: { status: 'PENDING' | 'APPROVED' | 'DENIED' | null; reasons: string[] };
    w9: { status: 'PENDING' | 'APPROVED' | 'DENIED' | null; reasons: string[] };
  };
  training: {
    submittedAt: string | null; approvedAt: string | null; changesRequestedAt: string | null; changesFeedback: string | null;
    jobType: 'REMOTE_SALES_REP' | 'FIELD_SALES_REP'; steps: string[]; currentStepKey: string | null; completedStepKeys: string[];
  };
  country: { code: string | null; name: string | null; eligibility: string; confirmed: boolean } | null;
  // Which market a field applicant works. `required` is false for the remote role, which has
  // none; on the field role a null `id` means the Location step is not finished yet.
  market: { required: boolean; id: string | null; name: string | null };
  application: { status: string; submittedAt: string | null; userVisibleExplanation: string | null };
  agreementSubmitted: boolean;
  taxFormSubmitted: boolean;
  requests: {
    leads: { status: string };
    grasshopper: { status: string };
    zoho: { status: string };
  };
  contractBroken: boolean;
}

interface ProgressState {
  progress: ProgressData | null;
  loading: boolean;
  /** False while the progress shown is still the copy saved from the last visit. */
  fresh: boolean;
  refresh: () => Promise<void>;
}

// The applicant's progress from the last visit (per user), shown at once while it reloads.
const progressKey = (userId: string | undefined) => (userId ? `krovance.apply.progress.v1:${userId}` : null);
function cachedProgress(key: string | null): ProgressData | null {
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as ProgressData) : null;
  } catch {
    return null;
  }
}

const ProgressContext = createContext<ProgressState | undefined>(undefined);

export function ProgressProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const key = progressKey(user?.id);
  const [progress, setProgress] = useState<ProgressData | null>(() => cachedProgress(key));
  const [loading, setLoading] = useState(() => !cachedProgress(key));
  const [fresh, setFresh] = useState(false);
  const hasLoadedProgress = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const result = await api.get<ProgressData>('/api/progress');
      setProgress(result);
      setFresh(true);
      hasLoadedProgress.current = true;
      try {
        if (key) localStorage.setItem(key, JSON.stringify(result));
      } catch {
        /* storage unavailable */
      }
    } catch {
      if (!hasLoadedProgress.current) setProgress(null);
    } finally {
      setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 10000);
    const refreshWhenVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refresh]);

  return <ProgressContext.Provider value={{ progress, loading, fresh, refresh }}>{children}</ProgressContext.Provider>;
}

export function useProgress(): ProgressState {
  const ctx = useContext(ProgressContext);
  if (!ctx) throw new Error('useProgress must be used within a ProgressProvider');
  return ctx;
}

/**
 * Same context, but undefined instead of throwing when there is no provider - for a page
 * that is normally rendered inside the onboarding flow but can also be rendered outside it
 * (the admin application preview, which has no applicant progress to load).
 */
export function useOptionalProgress(): ProgressState | undefined {
  return useContext(ProgressContext);
}

export function stepIndex(step: StepKey): number {
  return STEP_ORDER.indexOf(step);
}
